import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ReplayIcon from "@mui/icons-material/Replay";
import BrushIcon from "@mui/icons-material/Brush";
import ScheduleIcon from "@mui/icons-material/Schedule";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { toast } from "react-toastify";
import api from "../../api";
import SetQuestions from "./SetQuestions";
import ScheduleDialog from "./ScheduleDialog";
import SchedulesPanel from "./SchedulesPanel";
import StopIcon from "@mui/icons-material/Stop";
import {
  ConfirmGenerate,
  POLL_MS,
  QueueStatus,
  StateChip,
  TypeChip,
  VideoProgress,
  VISIBILITY_LABELS,
  money,
  moneyAuto,
} from "./shared";

const keyOf = (set) => `${set.type}-${set.id}`;

function FilterRow({ label, hint, children }) {
  return (
    <Stack direction="row" alignItems="flex-start" gap={0.75} sx={{ minWidth: 0, maxWidth: "100%" }}>
      <Tooltip title={hint || ""}>
        <Typography
          variant="caption"
          sx={{ width: 64, flexShrink: 0, fontWeight: 700, color: "text.secondary", lineHeight: "24px" }}
        >
          {label}
        </Typography>
      </Tooltip>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, minWidth: 0, flex: 1, "& > *": { flexShrink: 0 } }}>
        {children}
      </Box>
    </Stack>
  );
}

function FilterChip({ active, onClick, children }) {
  return (
    <Chip
      size="small"
      clickable
      label={children}
      onClick={onClick}
      color={active ? "primary" : "default"}
      variant={active ? "filled" : "outlined"}
      sx={{ fontWeight: active ? 700 : 500 }}
    />
  );
}

// Video status filter: only sets that have at least one video in that status.
const STATUS_OPTIONS = [
  { value: "", label: "Any videos" },
  { value: "awaiting_verification", label: "Awaiting verification" },
  { value: "verified_unpublished", label: "Verified, not published" },
  { value: "rejected", label: "Rejected by tester" },
  { value: "published", label: "Published" },
  { value: "finished", label: "Finished (any)" },
  { value: "missing", label: "Missing videos" },
  { value: "problems", label: "Failed / held / flagged" },
  { value: "in_progress", label: "Being made" },
];
const retryableOf = (c) => c.failed + c.needs_review + c.flagged;

function SetRow({ set, selected, onToggle, visibility, onGenerate, onStop, onChanged, onSchedule, onScheduleSet }) {
  const [open, setOpen] = useState(false);
  const { counts, total } = set;
  const done = counts.completed;
  const retryable = retryableOf(counts);

  return (
    <>
      <TableRow hover selected={selected}>
        <TableCell padding="checkbox">
          <Checkbox size="small" checked={selected} onChange={onToggle} inputProps={{ "aria-label": `Select ${set.name}` }} />
        </TableCell>
        <TableCell width={40}>
          <IconButton size="small" onClick={() => setOpen((o) => !o)} aria-label="Show questions">
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={600}>
            {set.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {[`#${set.id}`, set.subject, set.difficulty].filter(Boolean).join(" · ")}
          </Typography>
        </TableCell>
        <TableCell align="center" width={150}>
          <TypeChip type={set.type} />
        </TableCell>
        <TableCell width={200}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <VideoProgress counts={counts} total={total} />
            <Typography variant="caption" sx={{ minWidth: 44 }}>
              {done}/{total}
            </Typography>
          </Stack>
          {done > 0 && (
            <Tooltip title={set.verification?.rejected ? `${set.verification.rejected} rejected by a tester` : ""}>
              <Typography variant="caption" color={set.published < done ? "warning.main" : "success.main"} display="block">
                Verified {set.verification?.verified ?? 0}/{done} · Published {set.published}/{done}
              </Typography>
            </Tooltip>
          )}
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {["in_progress", "missing", "failed", "needs_review", "flagged"].map(
              (s) => counts[s] > 0 && <StateChip key={s} state={s} count={counts[s]} />
            )}
          </Stack>
        </TableCell>
        <TableCell width={110}>
          <Tooltip title="Estimated cost to make the missing, failed and held videos">
            <Typography variant="body2">{money(set.estimated_cost_usd)}</Typography>
          </Tooltip>
          {set.spent_usd > 0 && (
            <Tooltip title="Real money already spent on this set's videos (AI script, voice and rendering), every attempt included">
              <Typography variant="caption" color="text.secondary" display="block">
                Spent {moneyAuto(set.spent_usd)}
              </Typography>
            </Tooltip>
          )}
        </TableCell>
        <TableCell width={330} align="right">
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            {counts.in_progress > 0 && (
              <Tooltip title="Take this set's videos off the queue so they can be generated again">
                <Button size="small" variant="outlined" color="error" startIcon={<StopIcon />} onClick={() => onStop(set)}>
                  Stop {counts.in_progress}
                </Button>
              </Tooltip>
            )}
            {retryable > 0 && (
              <Button size="small" variant="outlined" startIcon={<ReplayIcon />} onClick={() => onGenerate([set], "failed")}>
                Retry {retryable}
              </Button>
            )}
            {counts.missing > 0 ? (
              <>
                <Tooltip title="Make this set's missing videos later, e.g. overnight">
                  <IconButton size="small" color="primary" onClick={() => onScheduleSet(set)} aria-label={`Schedule ${set.name}`}>
                    <ScheduleIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Button size="small" variant="contained" startIcon={<PlayArrowIcon />} onClick={() => onGenerate([set], "missing")}>
                  Generate {counts.missing}
                </Button>
              </>
            ) : (
              done > 0 &&
              counts.in_progress === 0 && (
                <Tooltip title="Make the finished videos again with a new presenter, design or music. Script and voice are reused, so it costs almost nothing.">
                  <Button size="small" variant="outlined" startIcon={<BrushIcon />} onClick={() => onGenerate([set], "restyle")}>
                    Re-style {done}
                  </Button>
                </Tooltip>
              )
            )}
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0, borderBottom: open ? undefined : "none" }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ py: 1.5 }}>
              <SetQuestions set={set} visibility={visibility} onChanged={onChanged} onSchedule={onSchedule} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function ExplanationVideos() {
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [subject, setSubject] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [status, setStatus] = useState("");
  // Videos cost money, so by default only sets students can actually open are listed.
  const [readyOnly, setReadyOnly] = useState(true);
  const [filterOptions, setFilterOptions] = useState({ subjects: [], difficulties: [] });
  const [matching, setMatching] = useState(null);
  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({}); // key -> set, kept across searches
  const [confirm, setConfirm] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [scheduleRequest, setScheduleRequest] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [visibility, setVisibility] = useState("approved");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [queue, setQueue] = useState(null);
  const requestId = useRef(0);
  const matchingId = useRef(0);

  // Difficulty belongs to worksheets only, so it is offered on All and Worksheets.
  const difficultyApplies = type === "" || type === "worksheet";

  // Filter choices are picked first and applied together with the Filter button.
  const DEFAULT_FILTERS = { subject: "", difficulty: "", status: "", readyOnly: true };
  const [draft, setDraft] = useState(DEFAULT_FILTERS);
  const applied = { subject, difficulty, status, readyOnly };
  const filterChanged = Object.keys(DEFAULT_FILTERS).some((k) => draft[k] !== applied[k]);
  const anyFilter = Object.keys(DEFAULT_FILTERS).some((k) => applied[k] !== DEFAULT_FILTERS[k]);
  const pickDraft = (field, value) => setDraft((d) => ({ ...d, [field]: value }));
  const applyFilters = () => {
    setSubject(draft.subject);
    setDifficulty(difficultyApplies ? draft.difficulty : "");
    setStatus(draft.status);
    setReadyOnly(draft.readyOnly);
  };
  const resetFilters = () => {
    setDraft(DEFAULT_FILTERS);
    setSubject("");
    setDifficulty("");
    setStatus("");
    setReadyOnly(true);
  };
  const filter = useMemo(
    () => ({
      search: search.trim() || undefined,
      type: type || undefined,
      subject: subject || undefined,
      difficulty: (difficultyApplies && difficulty) || undefined,
      ready: readyOnly ? 1 : undefined,
    }),
    [search, type, subject, difficulty, difficultyApplies, readyOnly]
  );
  const filtered = Object.values(filter).some(Boolean);

  const load = useCallback(
    async ({ quiet = false } = {}) => {
      const id = ++requestId.current;
      if (!quiet) setLoading(true);
      try {
        const res = await api.get("/admin/explanation-videos/sets", {
          params: { ...filter, status: status || undefined, page: page + 1, per_page: rowsPerPage },
        });
        if (id !== requestId.current) return;
        const data = res.data?.data?.items || [];
        setSets(data);
        setTotal(res.data?.data?.total || 0);
        // Refresh the counts of selected sets that are on screen.
        setSelected((sel) => {
          const next = { ...sel };
          data.forEach((s) => {
            if (next[keyOf(s)]) next[keyOf(s)] = s;
          });
          return next;
        });
      } catch {
        if (id === requestId.current && !quiet) toast.error("Could not load video coverage.");
      } finally {
        if (id === requestId.current) setLoading(false);
      }
    },
    [filter, status, page, rowsPerPage]
  );

  // Totals across every page of the filtered list, for "generate / schedule all".
  const loadMatching = useCallback(async () => {
    const id = ++matchingId.current;
    if (!filtered) {
      setMatching(null);
      return;
    }
    try {
      const res = await api.get("/admin/explanation-videos/matching", { params: filter });
      if (id === matchingId.current) setMatching(res.data?.data || null);
    } catch {
      if (id === matchingId.current) setMatching(null);
    }
  }, [filter, filtered]);

  // A new search, tab or filter starts again from the first page.
  useEffect(() => {
    setPage(0);
  }, [filter, status]);

  useEffect(() => {
    const t = setTimeout(() => loadMatching(), 400);
    return () => clearTimeout(t);
  }, [loadMatching]);

  const loadSchedules = useCallback(async () => {
    try {
      const res = await api.get("/admin/explanation-videos/schedules");
      setSchedules(res.data?.data || []);
    } catch {
      // The panel just stays hidden.
    }
  }, []);

  const loadQueue = useCallback(async () => {
    try {
      const res = await api.get("/admin/explanation-videos/queue");
      setQueue(res.data?.data || null);
    } catch {
      // The queue bar just stays hidden.
    }
  }, []);

  useEffect(() => {
    loadSchedules();
    loadQueue();
    api
      .get("/admin/explanation-videos/filters")
      .then((res) => setFilterOptions({ subjects: [], difficulties: [], ...res.data?.data }))
      .catch(() => {});
    api
      .get("/admin/explanation-videos/settings")
      .then((res) => setVisibility(res.data?.data?.visibility || "approved"))
      .catch(() => {});
  }, [loadSchedules, loadQueue]);

  // Search as the admin types, after a short pause.
  useEffect(() => {
    const t = setTimeout(() => load(), 400);
    return () => clearTimeout(t);
  }, [load]);

  // Keep counts fresh while videos are being made or a run is due.
  const queueBusy = Boolean(queue?.queues?.some((q) => q.waiting + q.working > 0));
  const running =
    queueBusy ||
    sets.some((s) => s.counts.in_progress > 0) ||
    schedules.some((s) => s.status === "scheduled" || s.status === "running");
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => {
      load({ quiet: true });
      loadMatching();
      loadSchedules();
      loadQueue();
    }, POLL_MS);
    return () => clearInterval(t);
  }, [running, load, loadMatching, loadSchedules, loadQueue]);

  const refresh = () => {
    load({ quiet: true });
    loadMatching();
    loadSchedules();
    loadQueue();
  };

  const generateMatching = (mode) =>
    setConfirm({
      filter,
      mode,
      count: mode === "failed" ? matching.retry : matching.missing,
      cost: mode === "failed" ? matching.estimated_retry_usd : matching.estimated_missing_usd,
      name: `${matching.label} (${matching.sets} sets)`,
      title: mode === "failed" ? "Retry videos" : "Generate videos",
      note: mode === "failed" ? "Failed, held and flagged videos get a new script from the AI." : null,
    });

  const scheduleMatching = (mode) =>
    setScheduleRequest({
      title: mode === "failed" ? "Schedule retries" : "Schedule missing videos",
      summary: `${mode === "failed" ? matching.retry : matching.missing} video(s) across ${matching.label} (${matching.sets} sets)`,
      cost: mode === "failed" ? matching.estimated_retry_usd : matching.estimated_missing_usd,
      filter,
      mode,
    });

  const scheduleSet = (set) =>
    setScheduleRequest({
      title: "Schedule missing videos",
      summary: `${set.counts.missing} video(s) of ${set.name}`,
      cost: set.estimated_missing_usd,
      items: [{ type: set.type, id: set.id }],
      mode: "missing",
    });

  const stopSet = async (set) => {
    if (!window.confirm(`Stop ${set.counts.in_progress} running video(s) of ${set.name}?`)) return;
    try {
      const res = await api.post(`/admin/explanation-videos/sets/${set.type}/${set.id}/stop`);
      toast.success(res.data?.message || "Stopped.");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not stop the videos.");
    }
  };

  const [stopping, setStopping] = useState(false);
  const stopSets = async () => {
    const list = selectedSets.filter((s) => s.counts.in_progress > 0);
    if (!window.confirm(`Stop ${totals.inProgress} running video(s) across ${describe(list)}? They can be generated again later.`)) return;
    setStopping(true);
    try {
      const res = await api.post("/admin/explanation-videos/bulk/stop", {
        items: list.map((s) => ({ type: s.type, id: s.id })),
      });
      toast.success(res.data?.message || "Stopped.");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not stop the videos.");
    } finally {
      setStopping(false);
    }
  };

  const stopAll = async () => {
    if (!window.confirm("Stop every video being made or waiting, in every set? They can be generated again later.")) return;
    setStopping(true);
    try {
      const res = await api.post("/admin/explanation-videos/queue/stop");
      toast.success(res.data?.message || "Stopped.");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not stop the videos.");
    } finally {
      setStopping(false);
    }
  };

  const selectedSets = useMemo(() => Object.values(selected), [selected]);
  const toggleSet = (set) =>
    setSelected((sel) => {
      const next = { ...sel };
      if (next[keyOf(set)]) delete next[keyOf(set)];
      else next[keyOf(set)] = set;
      return next;
    });
  const allOnScreenSelected = sets.length > 0 && sets.every((s) => selected[keyOf(s)]);
  const someOnScreenSelected = sets.some((s) => selected[keyOf(s)]);
  const toggleAllOnScreen = () =>
    setSelected((sel) => {
      const next = { ...sel };
      sets.forEach((s) => {
        if (allOnScreenSelected) delete next[keyOf(s)];
        else next[keyOf(s)] = s;
      });
      return next;
    });

  const totals = useMemo(
    () =>
      selectedSets.reduce(
        (acc, s) => ({
          missing: acc.missing + s.counts.missing,
          inProgress: acc.inProgress + (s.counts.in_progress || 0),
          retry: acc.retry + retryableOf(s.counts),
          completed: acc.completed + s.counts.completed,
          // Only videos a tester has verified can be published.
          unpublished: acc.unpublished + (s.verification?.verified_unpublished || 0),
          missingCost: acc.missingCost + (s.estimated_missing_usd || 0),
          retryCost: acc.retryCost + (s.estimated_retry_usd || 0),
        }),
        { missing: 0, inProgress: 0, retry: 0, completed: 0, unpublished: 0, missingCost: 0, retryCost: 0 }
      ),
    [selectedSets]
  );

  const setNames = useMemo(() => {
    const names = {};
    [...sets, ...selectedSets].forEach((s) => {
      names[keyOf(s)] = s.name;
    });
    return names;
  }, [sets, selectedSets]);

  const describe = (list) => (list.length === 1 ? list[0].name : `${list.length} sets`);

  const onGenerate = (list, mode) => {
    const countOf = (s) => (mode === "failed" ? retryableOf(s.counts) : mode === "restyle" ? s.counts.completed : s.counts.missing);
    const count = list.reduce((n, s) => n + countOf(s), 0);
    // A re-style keeps each video's script and voice, so it costs (almost) nothing.
    const cost =
      mode === "restyle" ? 0 : list.reduce((n, s) => n + ((mode === "failed" ? s.estimated_retry_usd : s.estimated_missing_usd) || 0), 0);
    setConfirm({
      list,
      mode,
      count,
      cost,
      name: describe(list),
      title: { failed: "Retry videos", restyle: "Re-style finished videos" }[mode] || "Generate videos",
      note: {
        failed: "Failed, held and flagged videos get a new script from the AI.",
        restyle:
          "Finished videos are made again with the style below, keeping their script and voice. The current video stays live until the new one is published.",
      }[mode] || null,
    });
  };

  const runGenerate = async (style = {}) => {
    const { list, filter: matchFilter, mode } = confirm;
    setSubmitting(true);
    try {
      const res = await api.post("/admin/explanation-videos/bulk/generate", {
        ...(matchFilter ? { filter: matchFilter } : { items: list.map((s) => ({ type: s.type, id: s.id })) }),
        mode,
        ...(Object.keys(style).length ? { style } : {}),
      });
      toast.success(res.data?.message || "Videos queued.");
      setConfirm(null);
      if (list && list.length > 1) setSelected({});
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not queue the videos.");
    } finally {
      setSubmitting(false);
    }
  };

  const scheduleSets = (mode) =>
    setScheduleRequest({
      title: mode === "failed" ? "Schedule retries" : "Schedule missing videos",
      summary: `${mode === "failed" ? totals.retry : totals.missing} video(s) across ${describe(selectedSets)}`,
      cost: mode === "failed" ? totals.retryCost : totals.missingCost,
      items: selectedSets.map((s) => ({ type: s.type, id: s.id })),
      mode,
      onDone: () => setSelected({}),
    });

  const publishSets = async () => {
    try {
      const res = await api.post("/admin/explanation-videos/bulk/publish", {
        items: selectedSets.map((s) => ({ type: s.type, id: s.id })),
        publish: true,
      });
      toast.success(res.data?.message || "Published.");
      load({ quiet: true });
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not publish the videos.");
    }
  };

  const changeVisibility = async (value) => {
    const previous = visibility;
    setVisibility(value);
    try {
      const res = await api.put("/admin/explanation-videos/settings", { visibility: value });
      toast.success(res.data?.message || "Saved.");
      load({ quiet: true });
    } catch (e) {
      setVisibility(previous);
      toast.error(e.response?.data?.message || "Could not save the setting.");
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      <Typography variant="caption" sx={{ color: "#374151", fontWeight: 700 }}>
        Dashboard /{" "}
        <Box component="span" sx={{ color: "#3B2A9F", fontWeight: 600 }}>
          Explanation Videos
        </Box>
      </Typography>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "flex-start" }} mt={0.5} mb={2}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>
            Explanation Videos
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Find a worksheet, mock exam or practice paper and make whiteboard videos for its questions, now or at a
            scheduled time. Filter by subject (and difficulty, for worksheets) to generate or schedule a whole group at
            once. Open a row to see each question, its cost, and who can watch it. A tester watches each finished
            video and approves or rejects it (here or in the Verification Dashboard); only approved videos can be
            published, and students and parents only ever see published videos. Admins can always watch every video.
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 260, bgcolor: "#fff" }}>
          <InputLabel id="video-visibility">Students &amp; parents see</InputLabel>
          <Select
            labelId="video-visibility"
            label="Students & parents see"
            value={visibility}
            onChange={(e) => changeVisibility(e.target.value)}
          >
            {Object.entries(VISIBILITY_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2}>
        <TextField
          size="small"
          placeholder="Search by name, e.g. Age problems"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, bgcolor: "#fff" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={type}
          onChange={(_, v) => v !== null && setType(v)}
          sx={{ bgcolor: "#fff" }}
        >
          <ToggleButton value="">All</ToggleButton>
          <ToggleButton value="worksheet">Worksheets</ToggleButton>
          <ToggleButton value="mock">Mock Exams</ToggleButton>
          <ToggleButton value="paper">Practice Papers</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* Filters: pick with the buttons, then press Filter to update the list. */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: "#fff", minWidth: 0 }}>
        <Stack spacing={1}>
          {/* Subject and Difficulty share a row. Videos spans the card so its chips wrap inside it. */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 1fr) minmax(0, 1fr)" },
              columnGap: 3,
              rowGap: 1,
              alignItems: "start",
              minWidth: 0,
            }}
          >
            <FilterRow label="Subject">
              <FilterChip active={!draft.subject} onClick={() => pickDraft("subject", "")}>All</FilterChip>
              {filterOptions.subjects.map((s) => (
                <FilterChip key={s.key} active={draft.subject === s.key} onClick={() => pickDraft("subject", s.key)}>
                  {s.name}
                </FilterChip>
              ))}
            </FilterRow>
            {difficultyApplies && (
              <FilterRow label="Difficulty" hint="Only worksheets have a difficulty, so picking one shows worksheets only">
                <FilterChip active={!draft.difficulty} onClick={() => pickDraft("difficulty", "")}>Any</FilterChip>
                {filterOptions.difficulties.map((d) => (
                  <FilterChip key={d} active={draft.difficulty === d} onClick={() => pickDraft("difficulty", d)}>
                    {d}
                  </FilterChip>
                ))}
              </FilterRow>
            )}
            <Box sx={{ gridColumn: { md: "1 / -1" }, minWidth: 0 }}>
              <FilterRow label="Videos">
                {STATUS_OPTIONS.map((o) => (
                  <FilterChip key={o.value} active={draft.status === o.value} onClick={() => pickDraft("status", o.value)}>
                    {o.label}
                  </FilterChip>
                ))}
              </FilterRow>
            </Box>
            <FilterRow label="Sets">
              <Tooltip title="Only Active sets that the Verification Dashboard shows as Fully verified on the web.">
                <span>
                  <FilterChip active={draft.readyOnly} onClick={() => pickDraft("readyOnly", true)}>
                    Active &amp; fully verified
                  </FilterChip>
                </span>
              </Tooltip>
              <FilterChip active={!draft.readyOnly} onClick={() => pickDraft("readyOnly", false)}>All sets</FilterChip>
            </FilterRow>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button size="small" variant="contained" startIcon={<SearchIcon />} disabled={!filterChanged} onClick={applyFilters}>
              Filter
            </Button>
            <Button size="small" onClick={resetFilters} disabled={!filterChanged && !anyFilter}>
              Reset
            </Button>
            {filterChanged && (
              <Typography variant="caption" color="warning.main">
                Press Filter to show the sets for these choices.
              </Typography>
            )}
          </Stack>
        </Stack>
      </Paper>

      {matching && matching.sets > 0 && (
        <Paper
          variant="outlined"
          sx={{ mb: 1.5, px: 2, py: 1, bgcolor: "#f0fdf4", borderColor: "#bbf7d0", display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
        >
          <Typography variant="body2" fontWeight={600}>
            {matching.label}: {matching.sets} set(s) · {matching.missing} missing · about {moneyAuto(matching.estimated_missing_usd)}
            {matching.retry > 0 && ` · ${matching.retry} to retry (${moneyAuto(matching.estimated_retry_usd)})`}
          </Typography>
          <Box sx={{ flex: 1 }} />
          {matching.retry > 0 && (
            <Button size="small" variant="outlined" startIcon={<ScheduleIcon />} onClick={() => scheduleMatching("failed")}>
              Schedule retries…
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={<ScheduleIcon />}
            disabled={matching.missing === 0}
            onClick={() => scheduleMatching("missing")}
          >
            Schedule all…
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<PlayArrowIcon />}
            disabled={matching.missing === 0 || submitting}
            onClick={() => generateMatching("missing")}
          >
            Generate all {matching.missing || ""}
          </Button>
        </Paper>
      )}

      <QueueStatus queue={queue} onStopAll={stopAll} stopping={stopping} />
      <SchedulesPanel schedules={schedules} setNames={setNames} onChanged={refresh} />

      {selectedSets.length > 0 && (
        <Paper
          variant="outlined"
          sx={{
            position: "sticky",
            top: 8,
            zIndex: 3,
            mb: 1.5,
            px: 2,
            py: 1,
            bgcolor: "#eef2ff",
            borderColor: "#c7d2fe",
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            {selectedSets.length} set(s) · {totals.missing} missing · about {moneyAuto(totals.missingCost)}
            {totals.retry > 0 && ` · ${totals.retry} to retry (${moneyAuto(totals.retryCost)})`}
            {totals.inProgress > 0 && ` · ${totals.inProgress} being made`}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={() => setSelected({})}>
            Clear
          </Button>
          {totals.inProgress > 0 && (
            <Tooltip title="Stop every video still being made in the selected sets. Queued jobs are removed and the videos show as missing again.">
              <span>
                <Button size="small" variant="outlined" color="error" startIcon={<StopIcon />} disabled={stopping} onClick={stopSets}>
                  Stop {totals.inProgress}
                </Button>
              </span>
            </Tooltip>
          )}
          {totals.unpublished > 0 && (
            <Button size="small" variant="outlined" color="success" startIcon={<VisibilityIcon />} onClick={publishSets}>
              Publish {totals.unpublished} verified
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={<ScheduleIcon />}
            disabled={totals.missing === 0}
            onClick={() => scheduleSets("missing")}
          >
            Schedule…
          </Button>
          <Tooltip title="Make finished videos again with a new presenter, design or music. Script and voice are reused, so it costs almost nothing.">
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<BrushIcon />}
                disabled={totals.completed === 0}
                onClick={() => onGenerate(selectedSets, "restyle")}
              >
                Re-style {totals.completed || ""}
              </Button>
            </span>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ReplayIcon />}
            disabled={totals.retry === 0}
            onClick={() => onGenerate(selectedSets, "failed")}
          >
            Retry {totals.retry || ""}
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<PlayArrowIcon />}
            disabled={totals.missing === 0 || submitting}
            onClick={() => onGenerate(selectedSets, "missing")}
          >
            Generate {totals.missing || ""}
          </Button>
        </Paper>
      )}

      <TableContainer component={Paper} variant="outlined">
        {loading && <LinearProgress />}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allOnScreenSelected}
                  indeterminate={!allOnScreenSelected && someOnScreenSelected}
                  disabled={sets.length === 0}
                  onChange={toggleAllOnScreen}
                  inputProps={{ "aria-label": "Select all sets" }}
                />
              </TableCell>
              <TableCell />
              <TableCell>Name</TableCell>
              <TableCell align="center">Type</TableCell>
              <TableCell>Videos</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>To finish</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {sets.map((set) => (
              <SetRow
                key={keyOf(set)}
                set={set}
                selected={Boolean(selected[keyOf(set)])}
                onToggle={() => toggleSet(set)}
                visibility={visibility}
                onGenerate={onGenerate}
                onStop={stopSet}
                onChanged={refresh}
                onSchedule={setScheduleRequest}
                onScheduleSet={scheduleSet}
              />
            ))}
            {!loading && sets.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                    {filtered || status ? "Nothing matches these filters." : "Nothing to show yet."}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      <ConfirmGenerate confirm={confirm} submitting={submitting} onClose={() => setConfirm(null)} onConfirm={runGenerate} />
      <ScheduleDialog
        request={scheduleRequest}
        onClose={() => setScheduleRequest(null)}
        onScheduled={() => {
          scheduleRequest?.onDone?.();
          setScheduleRequest(null);
          loadSchedules();
        }}
      />
    </Box>
  );
}
